import React from "react";

import {
    List,
    ListItem,
    ListItemText,
    Divider,
    Tooltip,
    Chip
} from "@material-ui/core";

import {
    getItemSymbol,
    getItemLookupSymbol
} from "../utils/symbols";

import { useLanguage } from "contexts/LanguageContext";
import { useAuth } from "../auth/AuthContext";

const DEFAULT_LANGUAGE = "en";

const SUPPORTED_LANGUAGES = [
    "en",
    "de",
    "fr",
    "es",
    "uk",
    "tr"
];

const LANGUAGE_LOCALES = {
    en: "en-GB",
    de: "de-DE",
    fr: "fr-FR",
    es: "es-ES",
    uk: "uk-UA",
    tr: "tr-TR"
};

const ALERT_LABELS = {
    en: {
        longAlerts: "LONG alerts",
        shortAlerts: "SHORT alerts"
    },
    de: {
        longAlerts: "LONG-Alarme",
        shortAlerts: "SHORT-Alarme"
    },
    fr: {
        longAlerts: "Alertes LONG",
        shortAlerts: "Alertes SHORT"
    },
    es: {
        longAlerts: "Alertas LONG",
        shortAlerts: "Alertas SHORT"
    },
    uk: {
        longAlerts: "LONG-сигнали",
        shortAlerts: "SHORT-сигнали"
    },
    tr: {
        longAlerts: "LONG uyarıları",
        shortAlerts: "SHORT uyarıları"
    }
};

function normalizeLanguage(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .split("-")[0];

    /*
     * ISO-Sprachcode Ukrainisch ist uk,
     * die Länderflagge verwendet dagegen UA.
     */
    if (normalized === "ua") {
        return "uk";
    }

    return SUPPORTED_LANGUAGES.includes(
        normalized
    )
        ? normalized
        : DEFAULT_LANGUAGE;
}
function normalizeTranslationLanguage(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .split("-")[0];

    /*
     * "org" darf niemals zu Englisch normalisiert werden.
     * Es bezeichnet ausschließlich die Originalsprache.
     */
    if (normalized === "org") {
        return "org";
    }

    if (normalized === "ua") {
        return "uk";
    }

    return SUPPORTED_LANGUAGES.includes(normalized)
        ? normalized
        : "";
}

function stripCodeFence(value) {
    if (!value || typeof value !== "string") {
        return "";
    }

    let text = value.trim();

    if (!text.startsWith("```")) {
        return text;
    }

    const lines = text.split(/\r?\n/);

    lines.shift();

    if (
        lines.length > 0 &&
        lines[lines.length - 1]
            .trim()
            .startsWith("```")
    ) {
        lines.pop();
    }

    return lines.join("\n").trim();
}

function safeParseTranslationsJson(value) {
    if (!value) {
        return [];
    }

    /*
     * JSON wurde von der API bereits als Array geliefert.
     */
    if (Array.isArray(value)) {
        return value;
    }

    /*
     * JSON wurde als Objekt geliefert.
     */
    if (typeof value === "object") {
        const possibleArrays = [
            value.translations,
            value.translation,
            value.items,
            value.data,
            value.values
        ];

        const existingArray =
            possibleArrays.find(Array.isArray);

        return existingArray || [];
    }

    if (typeof value !== "string") {
        return [];
    }

    let text = stripCodeFence(value);

    if (!text) {
        return [];
    }

    /*
     * Zuerst regulär parsen.
     */
    try {
        const parsed = JSON.parse(text);

        if (Array.isArray(parsed)) {
            return parsed;
        }

        if (parsed && typeof parsed === "object") {
            const possibleArrays = [
                parsed.translations,
                parsed.translation,
                parsed.items,
                parsed.data,
                parsed.values
            ];

            const existingArray =
                possibleArrays.find(Array.isArray);

            return existingArray || [];
        }
    } catch {
        /*
         * Danach versuchen wir, nur den Array-Teil
         * aus einem eventuell zusätzlichen Text auszuschneiden.
         */
    }

    const firstArrayCharacter =
        text.indexOf("[");

    const lastArrayCharacter =
        text.lastIndexOf("]");

    if (
        firstArrayCharacter === -1 ||
        lastArrayCharacter <=
        firstArrayCharacter
    ) {
        console.warn(
            "TranslationsJson enthält kein gültiges Array:",
            value
        );

        return [];
    }

    text = text.substring(
        firstArrayCharacter,
        lastArrayCharacter + 1
    );

    try {
        const parsed = JSON.parse(text);

        return Array.isArray(parsed)
            ? parsed
            : [];
    } catch (error) {
        console.warn(
            "TranslationsJson konnte nicht gelesen werden:",
            error,
            value
        );

        return [];
    }
}

function getTranslationsJson(newsItem) {
    if (!newsItem) {
        return null;
    }

    const directValue =
        newsItem.translationsJson ??
        newsItem.translationJson ??
        newsItem.TranslationsJson ??
        newsItem.TranslationJson ??
        newsItem.translationsJSON ??
        newsItem.translationJSON ??
        newsItem.translationsjson ??
        newsItem.translationjson ??
        newsItem.newsTranslationsJson ??
        newsItem.NewsTranslationsJson;

    if (directValue != null) {
        return directValue;
    }

    const nestedObjects = [
        newsItem.raw,
        newsItem.Raw,

        newsItem.newsRaw,
        newsItem.NewsRaw,

        newsItem.rawRecord,
        newsItem.RawRecord,

        newsItem.newsRawRecord,
        newsItem.NewsRawRecord,

        newsItem.news,
        newsItem.News,

        newsItem.source,
        newsItem.Source
    ];

    for (const nestedItem of nestedObjects) {
        if (!nestedItem || typeof nestedItem !== "object") {
            continue;
        }

        const nestedValue =
            nestedItem.translationsJson ??
            nestedItem.translationJson ??
            nestedItem.TranslationsJson ??
            nestedItem.TranslationJson ??
            nestedItem.translationsJSON ??
            nestedItem.translationJSON ??
            nestedItem.translationsjson ??
            nestedItem.translationjson;

        if (nestedValue != null) {
            return nestedValue;
        }
    }

    return null;
}

function findTranslation(
    translations,
    language,
    isGuest = false
) {
    if (!Array.isArray(translations)) {
        return null;
    }

    /*
     * Gäste erhalten ausschließlich den org-Eintrag.
     */
    if (isGuest) {
        return (
            translations.find(item => {
                const itemLanguage =
                    normalizeTranslationLanguage(
                        item?.lang ??
                        item?.language ??
                        item?.languageCode ??
                        item?.culture
                    );

                return itemLanguage === "org";
            }) || null
        );
    }

    const selectedLanguage =
        normalizeLanguage(language);

    /*
     * Angemeldeter Benutzer:
     * Zuerst exakt die ausgewählte Sprache suchen.
     *
     * Wichtig:
     * org bleibt org und wird nicht mehr als en behandelt.
     */
    const selectedTranslation =
        translations.find(item => {
            const itemLanguage =
                normalizeTranslationLanguage(
                    item?.lang ??
                    item?.language ??
                    item?.languageCode ??
                    item?.culture
                );

            return itemLanguage === selectedLanguage;
        });

    if (selectedTranslation) {
        return selectedTranslation;
    }

    /*
     * Fallback 1: Englisch
     */
    const englishTranslation =
        translations.find(item => {
            const itemLanguage =
                normalizeTranslationLanguage(
                    item?.lang ??
                    item?.language ??
                    item?.languageCode ??
                    item?.culture
                );

            return itemLanguage === DEFAULT_LANGUAGE;
        });

    if (englishTranslation) {
        return englishTranslation;
    }

    /*
     * Fallback 2: Originalsprache
     */
    return (
        translations.find(item => {
            const itemLanguage =
                normalizeTranslationLanguage(
                    item?.lang ??
                    item?.language ??
                    item?.languageCode ??
                    item?.culture
                );

            return itemLanguage === "org";
        }) || null
    );
}

function getLocalizedNews(
    newsItem,
    language,
    isGuest

) {
    const translations =
        safeParseTranslationsJson(
            getTranslationsJson(newsItem)
        );

    const selectedTranslation =
        findTranslation(
            translations,
            language,
            isGuest
        );

    const translatedTitle =
        selectedTranslation?.title ??
        selectedTranslation?.Title ??
        selectedTranslation?.headline ??
        selectedTranslation?.Headline ??
        "";

    const translatedSummary =
        selectedTranslation?.summary ??
        selectedTranslation?.Summary ??
        selectedTranslation?.description ??
        selectedTranslation?.Description ??
        "";

    return {
        title:
            String(translatedTitle || "").trim() ||
            String(
                newsItem?.title ??
                newsItem?.Title ??
                newsItem?.headline ??
                newsItem?.Headline ??
                ""
            ).trim(),

        summary:
            String(translatedSummary || "").trim() ||
            String(
                newsItem?.summary ??
                newsItem?.Summary ??
                newsItem?.description ??
                newsItem?.Description ??
                ""
            ).trim(),

        translationFound:
            Boolean(selectedTranslation),

        availableLanguages:
            translations
                .map(item =>
                    normalizeLanguage(
                        item?.lang ??
                        item?.language ??
                        item?.languageCode
                    )
                )
                .filter(Boolean)
    };
}

function parsePublishedDate(value) {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : value;
    }

    const rawValue = String(value).trim();

    if (!rawValue) {
        return null;
    }

    const normalizedValue =
        rawValue.includes("T")
            ? rawValue
            : rawValue.replace(" ", "T");

    const hasTimezone =
        /Z$/i.test(normalizedValue) ||
        /[+-]\d{2}:\d{2}$/.test(
            normalizedValue
        );

    const parsed = new Date(
        hasTimezone
            ? normalizedValue
            : `${normalizedValue}Z`
    );

    return Number.isNaN(parsed.getTime())
        ? null
        : parsed;
}

function formatPublishedAt(
    value,
    language
) {
    if (!value) {
        return "";
    }

    const parsed = parsePublishedDate(value);

    if (!parsed) {
        return String(value);
    }

    const locale =
        LANGUAGE_LOCALES[language] ||
        LANGUAGE_LOCALES[DEFAULT_LANGUAGE];

    return parsed.toLocaleString(locale, {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    });
}

export default function NewsList({
    items = [],
    selectedId,
    onSelect,
    alertSummaryMap = {}
}) {
    const { language } = useLanguage();
    const { isGuest } = useAuth();

    /*
     * Gäste sehen unabhängig von einer möglicherweise
     * gespeicherten Auswahl immer Englisch.
     */
    const selectedLanguage = isGuest
        ? DEFAULT_LANGUAGE
        : normalizeLanguage(language);

    const alertLabels =
        ALERT_LABELS[selectedLanguage] ||
        ALERT_LABELS[DEFAULT_LANGUAGE];

    const safeItems = Array.isArray(items)
        ? items
        : [];

    return (
        <List
            style={{
                maxHeight:
                    "calc(100vh - 200px)",
                overflowY: "auto"
            }}
        >
            {safeItems.map(newsItem => {

                console.log("NewsList item:", newsItem);
                console.log(
                    "NewsList TranslationsJson:",
                    getTranslationsJson(newsItem)
                );

                const localizedNews =
                    getLocalizedNews(
                        newsItem,
                        selectedLanguage,
                        isGuest
                    );

                const symbol =
                    getItemSymbol(newsItem);

                const lookupSymbol =
                    getItemLookupSymbol(newsItem);

                const alertSummary =
                    lookupSymbol
                        ? alertSummaryMap[
                        lookupSymbol
                        ]
                        : null;

                const hasAlerts =
                    Boolean(alertSummary) &&
                    (
                        Number(
                            alertSummary.longCount
                        ) > 0 ||
                        Number(
                            alertSummary.shortCount
                        ) > 0
                    );

                const itemId =
                    newsItem?.id ??
                    newsItem?.Id;

                const provider =
                    newsItem?.provider ??
                    newsItem?.Provider ??
                    "";

                const publishedAt =
                    newsItem?.publishedAt ??
                    newsItem?.PublishedAt;

                return (
                    <React.Fragment
                        key={itemId}
                    >
                        <ListItem
                            button
                            selected={
                                itemId === selectedId
                            }
                            onClick={() => {
                                if (
                                    typeof onSelect ===
                                    "function"
                                ) {
                                    onSelect(itemId);
                                }
                            }}
                            style={{
                                borderLeft: hasAlerts
                                    ? "4px solid #2196f3"
                                    : "4px solid transparent",

                                paddingLeft: 12
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                    gap: 12
                                }}
                            >
                                <ListItemText
                                    primary={
                                        localizedNews.title
                                    }
                                    secondary={
                                        <div
                                            style={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                flexWrap:
                                                    "wrap",
                                                marginTop: 2,
                                                gap: 8
                                            }}
                                        >
                                            {provider && (
                                                <span
                                                    style={{
                                                        background:
                                                            "#e3f2fd",
                                                        color:
                                                            "#0d47a1",
                                                        padding:
                                                            "2px 8px",
                                                        borderRadius:
                                                            4,
                                                        fontSize:
                                                            11,
                                                        fontWeight:
                                                            600
                                                    }}
                                                >
                                                    {provider}
                                                </span>
                                            )}

                                            {symbol && (
                                                <span
                                                    style={{
                                                        background:
                                                            "#f3e5f5",
                                                        color:
                                                            "#6a1b9a",
                                                        padding:
                                                            "2px 8px",
                                                        borderRadius:
                                                            4,
                                                        fontSize:
                                                            11,
                                                        fontWeight:
                                                            600
                                                    }}
                                                >
                                                    {symbol}
                                                </span>
                                            )}

                                            <span
                                                style={{
                                                    color:
                                                        "#777",
                                                    fontSize:
                                                        12
                                                }}
                                            >
                                                {formatPublishedAt(
                                                    publishedAt,
                                                    selectedLanguage
                                                )}
                                            </span>
                                        </div>
                                    }
                                />

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        gap: 6,
                                        flexShrink: 0
                                    }}
                                >
                                    {Number(
                                        alertSummary
                                            ?.longCount
                                    ) > 0 && (
                                            <Tooltip
                                                title={
                                                    alertSummary
                                                        ?.companyName
                                                        ? `${alertSummary.companyName} – ${alertLabels.longAlerts}: ${alertSummary.longCount}`
                                                        : `${alertLabels.longAlerts}: ${alertSummary.longCount}`
                                                }
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`▲ ${alertSummary.longCount}`}
                                                    style={{
                                                        backgroundColor:
                                                            "#4caf50",
                                                        color:
                                                            "#fff",
                                                        fontWeight:
                                                            600
                                                    }}
                                                />
                                            </Tooltip>
                                        )}

                                    {Number(
                                        alertSummary
                                            ?.shortCount
                                    ) > 0 && (
                                            <Tooltip
                                                title={
                                                    alertSummary
                                                        ?.companyName
                                                        ? `${alertSummary.companyName} – ${alertLabels.shortAlerts}: ${alertSummary.shortCount}`
                                                        : `${alertLabels.shortAlerts}: ${alertSummary.shortCount}`
                                                }
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`▼ ${alertSummary.shortCount}`}
                                                    style={{
                                                        backgroundColor:
                                                            "#f44336",
                                                        color:
                                                            "#fff",
                                                        fontWeight:
                                                            600
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                </div>
                            </div>
                        </ListItem>

                        <Divider />
                    </React.Fragment>
                );
            })}
        </List>
    );
}